import React from 'react';
import { MainRouter } from 'app/shared/components';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import createCache from '@emotion/cache';
import { TssCacheProvider } from 'tss-react';
import { theme } from 'app/shared/theme/theme';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from 'app/shared/config';
import {
  AuthProvider,
  ErrorProvider,
  ModalProvider,
  NotificationSettingsProvider,
} from 'app/shared/providers';

const queryCache = new QueryCache();
const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      throwOnError: false,
      retry: 1,
      gcTime: 1,
    },
    mutations: {
      throwOnError: false,
    },
  },
});

// tss-react (makeStyles/withStyles) gets its own emotion cache. Its styles are
// then inserted after MUI's (prepended by StyledEngineProvider) instead of being
// merged into MUI's classes, so app styles win over MUI defaults as in v4.
const tssCache = createCache({ key: 'tss' });

const msalInstance = new PublicClientApplication(msalConfig);

/**
 * MSAL v4+ takes `navigateToLoginRequestUrl` when handling the redirect instead of
 * in the config. `handleRedirectPromise` memoizes its first call, so handling it
 * here before <MsalProvider> mounts makes the provider reuse this result.
 */
export const msalReady = msalInstance.initialize().then(() =>
  msalInstance.handleRedirectPromise({
    navigateToLoginRequestUrl: !!localStorage.getItem('redirectUrl'),
  }),
);

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <StyledEngineProvider injectFirst>
          <TssCacheProvider value={tssCache}>
            <ThemeProvider theme={theme}>
              <ErrorProvider>
                <ModalProvider>
                  <NotificationSettingsProvider>
                    <QueryClientProvider client={queryClient}>
                      <MainRouter />
                    </QueryClientProvider>
                  </NotificationSettingsProvider>
                </ModalProvider>
              </ErrorProvider>
            </ThemeProvider>
          </TssCacheProvider>
        </StyledEngineProvider>
      </AuthProvider>
    </MsalProvider>
  );
}
